import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { reglaPropinaService, ReglaPropina, ReglaPropinaCreate, ReglaPropinaUpdate } from '../../services/reglaPropinaService';
import { sucursalService } from '../../services/sucursalService';

interface ReglaPropinaFormProps {
  regla?: ReglaPropina | null;
  onClose: () => void;
}

const ReglaPropinaForm = ({ regla, onClose }: ReglaPropinaFormProps) => {
  const queryClient = useQueryClient();

  const { data: sucursalesData } = useQuery(
    'sucursales',
    () => sucursalService.getAll(1, 100)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<ReglaPropinaCreate | ReglaPropinaUpdate>({
    defaultValues: regla || {
      name: '',
      tipo: 'porcentaje',
      valor: 0,
      sucursalId: undefined,
      minimoOrden: undefined,
      maximoOrden: undefined
    }
  });

  const tipo = watch('tipo');

  useEffect(() => {
    if (regla) {
      reset({
        name: regla.name,
        tipo: regla.tipo,
        valor: regla.valor,
        sucursalId: regla.sucursalId,
        minimoOrden: regla.minimoOrden,
        maximoOrden: regla.maximoOrden
      });
    }
  }, [regla, reset]);

  const createMutation = useMutation(reglaPropinaService.create, {
    onSuccess: () => {
      toast.success('Regla de propina creada correctamente');
      queryClient.invalidateQueries(['reglas-propinas']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear regla de propina');
    }
  });

  const updateMutation = useMutation(
    (data: ReglaPropinaUpdate) => reglaPropinaService.update(regla!.id, data),
    {
      onSuccess: () => {
        toast.success('Regla de propina actualizada correctamente');
        queryClient.invalidateQueries(['reglas-propinas']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar regla de propina');
      }
    }
  );

  const onSubmit = (data: ReglaPropinaCreate | ReglaPropinaUpdate) => {
    // Convertir valores opcionales
    const submitData = {
      ...data,
      sucursalId: data.sucursalId || undefined,
      minimoOrden: data.minimoOrden || undefined,
      maximoOrden: data.maximoOrden || undefined
    };

    if (regla) {
      updateMutation.mutate(submitData as ReglaPropinaUpdate);
    } else {
      createMutation.mutate(submitData as ReglaPropinaCreate);
    }
  };

  const sucursales = sucursalesData?.data.sucursales || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {regla ? 'Editar Regla de Propina' : 'Nueva Regla de Propina'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              type="text"
              {...register('name', { required: 'Nombre es requerido' })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo *
            </label>
            <select
              {...register('tipo')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="porcentaje">Porcentaje</option>
              <option value="fijo">Fijo</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor * {tipo === 'porcentaje' ? '(%)' : '($)'}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max={tipo === 'porcentaje' ? '100' : undefined}
              {...register('valor', {
                required: 'Valor es requerido',
                min: { value: 0, message: 'Debe ser mayor o igual a 0' },
                max: tipo === 'porcentaje' ? { value: 100, message: 'Debe ser menor o igual a 100' } : undefined
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.valor && (
              <p className="text-red-500 text-xs mt-1">{errors.valor.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sucursal (opcional, dejar vacío para global)
            </label>
            <select
              {...register('sucursalId', { valueAsNumber: true })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Global (Todas las sucursales)</option>
              {sucursales.map((sucursal) => (
                <option key={sucursal.id} value={sucursal.id}>
                  {sucursal.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto Mínimo de Orden (opcional)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('minimoOrden', { valueAsNumber: true })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monto Máximo de Orden (opcional)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('maximoOrden', { valueAsNumber: true })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {regla && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Activo</span>
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              {regla ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReglaPropinaForm;

